import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useHistory, Link, RouteChildrenProps, generatePath } from 'react-router-dom'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import API from '../../../../services/api'
import useAPIRequest from '../../../../hooks/useAPIRequest'
import Container from '../../../atoms/page/Page'
import Input from '../../../atoms/form/input/Input'
import InlineGroup from '../../../atoms/inlinegroup/InlineGroup'
import LoadingSpinner from '../../../atoms/loadingspinner/LoadingSpinner'
import DeleteParticipant from './DeleteParticipant'
import DisplayField from '../../../atoms/form/display-field/DisplayField'
import Form from '../../../atoms/form/Form'
import Banner from '../../../atoms/banner/Banner'
import IconButton from '../../../molecules/iconbutton/IconButton'
import Button from '../../../atoms/button/Button'
import VerticalGroup from '../../../atoms/verticalgroup/VerticalGroup'
import Text from '../../../atoms/text/Text'
import openNewTab from '../../../../lib/openNewTab'

interface ParticipantState {
  deleteModalOpen: boolean
}

function ParticipantDetails({ match }: RouteChildrenProps) {
  let { eventId, participantId } = useParams<{ eventId: string, participantId: string }>()
  const [selectedParticipantId, setSelectedParticipantId] = useState(participantId)

  const history = useHistory()

  const callback = useCallback(() => {
    return API.getEvent(eventId)
  }, [eventId])

  const [{ inProgress, data, error }, doAPIRequest] = useAPIRequest<API.EventResponse>(callback, null)

  useEffect(() => {
    doAPIRequest()
  }, [doAPIRequest])

  const [{ deleteModalOpen }, setParticipantState] = useState<ParticipantState>({
    deleteModalOpen: false
  })

  const onDeleteParticipantComplete = useCallback(() => {
    setParticipantState(prevState => ({ ...prevState, deleteModalOpen: false }))
    history.push(`/events/${eventId}/view`)
  }, [history, eventId])

  const participant: API.ParticipantResponse | undefined = useMemo(() => {
    if (!data) {
      return undefined
    }
    return data.participants.find(p => p.id === selectedParticipantId)
  }, [selectedParticipantId, data])

  const nextParticipant = useCallback(() => {
    if (!data) {
      return
    }
    const currentIndex = data.participants.findIndex(participant => participant.id === selectedParticipantId)
    let nextIndex = currentIndex + 1
    if (nextIndex >= data.participants.length) {
      nextIndex = 0
    }
    setSelectedParticipantId(data.participants[nextIndex].id)
    setCopied(false)
    history.push(generatePath(match!.path, {
      eventId,
      participantId: data.participants[nextIndex].id
    }))
  }, [data, selectedParticipantId, setSelectedParticipantId, match, eventId, history])

  const previousParticipant = useCallback(() => {
    if (!data) {
      return
    }
    const currentIndex = data.participants.findIndex(participant => participant.id === selectedParticipantId)
    let nextIndex = currentIndex - 1
    if (nextIndex < 0) {
      nextIndex = data.participants.length - 1
    }
    setSelectedParticipantId(data.participants[nextIndex].id)
    setCopied(false)
    history.push(generatePath(match!.path, {
      eventId,
      participantId: data.participants[nextIndex].id
    }))
  }, [data, selectedParticipantId, setSelectedParticipantId, match, eventId, history])

  const password = participant?.id.split('-')[0]

  const [copied, setCopied] = useState(false)
  const [copiedLogin, setCopiedLogin] = useState(false)

  const participantLoginLink = useMemo(() => {
    if (participant != null) {
      return `${process.env.REACT_APP_GAME_URL}?eventId=${encodeURIComponent(eventId)}&participantId=${encodeURIComponent(participant.id)}&email=${encodeURIComponent(participant.email)}`
    }
    return ''
  }, [eventId, participant])

  return (
    <>
      {deleteModalOpen && !!participant && <DeleteParticipant
        isOpen={deleteModalOpen}
        onClose={() => setParticipantState(prevState => ({ ...prevState, deleteModalOpen: false }))}
        onComplete={onDeleteParticipantComplete}
        eventId={eventId}
        participants={[participant]}
      />}

      <Container>
        <VerticalGroup spread full>
          <VerticalGroup wide>
            {error && <Banner type='error' active={!!error} message={error?.message} />}
            <InlineGroup spread verticalCenter block>
              <h2>Participant Details</h2>
              {!!participant && !inProgress && <InlineGroup spread spaceLeft={2}>
                <IconButton
                  data-test={`edit-participant-${participant.id}`}
                  icon='edit'
                  text='Edit Participant'
                  linkTo={`/events/${eventId}/participants/${participant.id}/edit`}
                />
                <IconButton
                  data-test={`login-participant-${participant.id}`}
                  icon='export'
                  text='Login As Participant'
                  onClick={() => openNewTab(participantLoginLink)}
                />
                <CopyToClipboard text={participantLoginLink} onCopy={() => setCopiedLogin(true)}>
                  <IconButton
                    icon='copy'
                    text='Copy Login Link'
                  />
                </CopyToClipboard>
                <IconButton
                  danger
                  data-test={`delete-participant-${participant.id}`}
                  icon='trash'
                  text='Delete'
                  onClick={() => setParticipantState(prevState => ({ ...prevState, deleteModalOpen: true }))}
                />
              </InlineGroup>}
            </InlineGroup>
            {inProgress && <LoadingSpinner />}
            {!!participant && !inProgress && (
              <>
                <Form>
                  <Input data-test='email' type='text' value={participant.email} label='Email' readOnly />
                  <Input data-test='firstName' type='text' value={participant.firstName} label='First Name' readOnly />
                  <Input data-test='lastName' type='text' value={participant.lastName} label='Last Name' readOnly />
                  <Input data-test='team' type='text' value={participant.team} label='Team #' readOnly />
                  <Input data-test='isCeo' type='text' value={participant.isCeo ? 'Yes' : 'No'} label='CEO' readOnly />
                  <InlineGroup>
                    <DisplayField data-test='participantPassword' label='Password' value={
                      <>
                        <InlineGroup spread>
                          <Text size='sm'>{password || ''}</Text>
                          <CopyToClipboard text={password || ''} onCopy={() => setCopied(true)}>
                            <IconButton
                              icon='copy'
                              text='Copy'
                            />
                          </CopyToClipboard>
                        </InlineGroup>
                      </>
                    } />
                  </InlineGroup>
                  <DisplayField data-test='participantId' label='Participant ID' value={participant.id} />
                  {copied && <Banner type='success' active={!!copied} message='Password copied' />}
                  {copiedLogin && <Banner type='success' active={!!copiedLogin} message='Login link copied' />}
                </Form>
              </>
            )}
          </VerticalGroup>
          <InlineGroup block spread>
            <Link data-test='back' to={`/events/${eventId}/view`}>
              <Button light wide>Back</Button>
            </Link>
            <InlineGroup spaceLeft={2}>
              <Button
                data-test='previous'
                type='submit'
                wide
                onClick={previousParticipant}
              >Previous
              </Button>
              <Button
                data-test='next'
                type='submit'
                wide
                onClick={nextParticipant}
              >Next
              </Button>
            </InlineGroup>
          </InlineGroup>
        </VerticalGroup>
      </Container>
    </>
  )
}

export default ParticipantDetails
